import React, { useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  useLoadCompanyDirectoryFullQuery,
  useUpsertCompanyDirectoryEntryMutation,
  useDeleteCompanyDirectoryEntryMutation,
  useUploadCompanyDirectoryPhotoMutation,
  useSetDirectoryActiveMutation,
  useListFirmsQuery,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

// In-house forms HR can grant per employee. Keys must match the backend form
// catalog (api/app/routers/forms.py). Role Alignment is intentionally first.
const FORMS_CATALOG = [
  { key: 'role_alignment', label: 'Role Alignment' },
];

function initialsOf(first, last) {
  const a = (first || '').trim().charAt(0).toUpperCase();
  const b = (last  || '').trim().charAt(0).toUpperCase();
  return (a + b) || '?';
}

// Live-format a US phone number as the user types. Keeps the input forgiving:
// any non-digit the user pastes is stripped, and we cap at 10 digits — extra
// trailing digits are dropped rather than throwing. Output shapes:
//   ''                      while empty
//   '(555'                  while typing the area code
//   '(555) 12'              while typing the prefix
//   '(555) 123-4567'        once all 10 digits are in
function formatPhoneInput(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// "Board of Directors" — top of the org chart. This value only ever exists inside
// the <select>; on save it is translated into `reports_to_board: true` with a null
// manager_id. It is deliberately NOT a numeric sentinel: manager_id is FK-
// constrained to a real directory row, so the database would reject one.
const BOARD_OPTION = '__BOARD__';
const BOARD_MANAGER_NAME = 'Board of Directors';

const blankForm = {
  id: null,
  first_name: '',
  last_name: '',
  final_email: '',
  prior_email: '',
  prior_company: '',
  title: '',
  firm: '',
  location: '',
  phone: '',
  hire_date: '',
  date_of_birth: '',
  is_manager: false,
  manager_id: '',
  can_own_projects: false,
  is_advisor: false,
  show_calendar: false,
  is_active: true,
  enabled_forms: [],
};

const inputStyle = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};
const labelStyle = {
  fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px',
  color: brand.charcoal, fontWeight: 700, marginBottom: 4, display: 'block',
};

function EditModal({ initial, onClose, onSave, saving, error, firms, managers, onUploadPhoto }) {
  const [form, setForm] = useState(initial);
  const [photoUrl, setPhotoUrl] = useState(initial.photo_url || null);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const photoInputRef = useRef(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const update = (k) => (e) => set(k, e.target.value);

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const handlePhoto = async (file) => {
    setPhotoError(null);
    setUploading(true);
    try {
      const result = await onUploadPhoto(file);
      if (result?.photo_url) setPhotoUrl(result.photo_url);
    } catch (err) {
      setPhotoError(err?.data?.detail || 'Photo upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '1rem',
    }}>
      <form onSubmit={submit} style={{ background: 'white', borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', margin: 0 }}>
            {form.id ? 'Edit Employee' : 'Add Employee'}
          </h5>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.1rem' }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
        <div style={{ padding: '1rem 1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', padding: '0.75rem 1rem', background: brand.softGray, borderRadius: 8 }}>
            {photoUrl ? (
              <img src={photoUrl} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid white' }} />
            ) : (
              <div style={{
                width: 72, height: 72, borderRadius: '50%', background: brand.tealBlue, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.2rem', flexShrink: 0, border: '2px solid white',
              }}>
                {initialsOf(form.first_name, form.last_name)}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, marginBottom: 4 }}>
                Photo
              </div>
              {form.id ? (
                <>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploading}
                    style={{
                      background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
                      padding: '6px 12px', fontWeight: 600, fontSize: '0.8rem',
                      cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1,
                    }}
                  >
                    <i className={`bi ${uploading ? 'bi-arrow-clockwise' : 'bi-upload'} me-1`} />
                    {uploading ? 'Uploading…' : (photoUrl ? 'Replace Photo' : 'Upload Photo')}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePhoto(file);
                      e.target.value = '';
                    }}
                  />
                  <div style={{ fontSize: '0.72rem', color: brand.charcoal, marginTop: 4 }}>
                    JPG, PNG, or WebP. Max 5 MB.
                  </div>
                </>
              ) : (
                <div style={{ fontSize: '0.78rem', color: brand.charcoal }}>
                  Save the employee first, then come back to upload a photo.
                </div>
              )}
              {photoError && (
                <div style={{ marginTop: 4, color: '#b00020', fontSize: '0.78rem' }}>
                  <i className="bi bi-exclamation-triangle me-1" />{photoError}
                </div>
              )}
            </div>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label style={labelStyle}>First Name *</label>
              <input style={inputStyle} value={form.first_name} onChange={update('first_name')} required />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Last Name *</label>
              <input style={inputStyle} value={form.last_name} onChange={update('last_name')} required />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Title</label>
              <input style={inputStyle} value={form.title} onChange={update('title')} placeholder="e.g. Senior Advisor" />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Firm</label>
              <select style={inputStyle} value={form.firm} onChange={update('firm')}>
                <option value="">—</option>
                {firms.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Location</label>
              <input style={inputStyle} value={form.location} onChange={update('location')} placeholder="City, ST" />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Phone</label>
              <input
                style={inputStyle}
                type="tel"
                inputMode="tel"
                value={form.phone || ''}
                onChange={(e) => set('phone', formatPhoneInput(e.target.value))}
                placeholder="(555) 123-4567"
                maxLength={14}
              />
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Hire Date</label>
              <input style={inputStyle} type="date" value={form.hire_date || ''} onChange={update('hire_date')} />
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Date of Birth</label>
              <input style={inputStyle} type="date" value={form.date_of_birth || ''} onChange={update('date_of_birth')} />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Email *</label>
              <input style={inputStyle} type="email" value={form.final_email} onChange={update('final_email')} required />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Former Email</label>
              <input style={inputStyle} type="email" value={form.prior_email} onChange={update('prior_email')} />
            </div>
            <div className="col-md-12">
              <label style={labelStyle}>Prior Company</label>
              <input style={inputStyle} value={form.prior_company} onChange={update('prior_company')} />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>Manager</label>
              <select
                style={inputStyle}
                value={form.manager_id || ''}
                onChange={update('manager_id')}
              >
                <option value="">— None —</option>
                {/* Top of the org chart. Listed first because it sits above
                    everyone, and it is not filtered against `form.id` the way
                    real managers are — nobody can be their own Board. */}
                <option value={BOARD_OPTION}>{BOARD_MANAGER_NAME}</option>
                {managers
                  .filter(m => m.id !== form.id)
                  .map(m => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                  ))}
              </select>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: brand.deepBlue, fontWeight: 600, paddingBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={!!form.is_manager}
                  onChange={(e) => set('is_manager', e.target.checked)}
                />
                Is a manager (can be selected as a manager of others)
              </label>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: brand.deepBlue, fontWeight: 600, paddingBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={!!form.can_own_projects}
                  onChange={(e) => set('can_own_projects', e.target.checked)}
                />
                Enable Project Owner (can be assigned to Tech projects)
              </label>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: brand.deepBlue, fontWeight: 600, paddingBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={!!form.is_advisor}
                  onChange={(e) => set('is_advisor', e.target.checked)}
                />
                Is an advisor (can be assigned to clients)
              </label>
            </div>
            <div className="col-md-12 d-flex align-items-end">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: brand.deepBlue, fontWeight: 600, paddingBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={!!form.show_calendar}
                  onChange={(e) => set('show_calendar', e.target.checked)}
                />
                Show Calendar
              </label>
              <div style={{ fontSize: '0.78rem', color: brand.charcoal, paddingBottom: 7, marginLeft: '0.6rem' }}>
                Gives access to the shared Outlook calendars page, where meetings can be
                created, changed and deleted — which emails the attendees. Intended for
                admins who support advisors.
              </div>
            </div>
          </div>

          {/* ── Forms ──────────────────────────────────────────────────────────
              Checking a form makes it visible to this employee under
              To-Do → Forms. */}
          <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: brand.softGray, borderRadius: 8 }}>
            <div style={{ ...labelStyle, marginBottom: 8 }}>
              <i className="bi bi-ui-checks me-1" /> Forms
            </div>
            <div style={{ fontSize: '0.78rem', color: brand.charcoal, marginBottom: 8 }}>
              Check a form to make it available to this employee under To-Do → Forms.
            </div>
            {FORMS_CATALOG.map((f) => {
              const enabled = (form.enabled_forms || []).includes(f.key);
              return (
                <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: brand.deepBlue, fontWeight: 600, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => {
                      const cur = form.enabled_forms || [];
                      set('enabled_forms', e.target.checked ? [...cur, f.key] : cur.filter((k) => k !== f.key));
                    }}
                  />
                  {f.label}
                </label>
              );
            })}
          </div>
          {error && (
            <div style={{ marginTop: '0.75rem', color: '#b00020', fontSize: '0.85rem' }}>
              <i className="bi bi-exclamation-triangle me-1" />{error}
            </div>
          )}
        </div>
        <div style={{ padding: '0.75rem 1.25rem', borderTop: `1px solid ${brand.softGray}`, display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ background: 'white', border: `1px solid ${brand.softGray}`, color: brand.charcoal, padding: '6px 14px', borderRadius: 6, fontWeight: 600, fontSize: '0.85rem' }}>
            Cancel
          </button>
          <button type="submit" disabled={saving} style={{ background: brand.deepBlue, border: 'none', color: 'white', padding: '6px 14px', borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
EditModal.propTypes = {
  initial: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
  firms: PropTypes.array.isRequired,
  managers: PropTypes.array.isRequired,
  onUploadPhoto: PropTypes.func.isRequired,
};

function PhotoCell({ emp, onUpload, uploading }) {
  const inputRef = useRef(null);
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      {emp.photo_url ? (
        <img
          src={emp.photo_url}
          alt={`${emp.first_name} ${emp.last_name}`}
          style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <div style={{
          width: 48, height: 48, borderRadius: '50%', background: brand.tealBlue, color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem',
        }}>
          {initialsOf(emp.first_name, emp.last_name)}
        </div>
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        title="Upload photo"
        style={{
          position: 'absolute', bottom: -2, right: -2, width: 22, height: 22, borderRadius: '50%',
          background: brand.goldenrod, color: 'white', border: '2px solid white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem',
        }}
      >
        <i className={`bi ${uploading ? 'bi-arrow-clockwise' : 'bi-camera-fill'}`} />
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
PhotoCell.propTypes = {
  emp: PropTypes.object.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
};

export default function HRDirectory() {
  // Active-only by default; the "show terminated" toggle re-queries with the
  // terminated employees included.
  const [showTerminated, setShowTerminated] = useState(false);
  const { data: rows = [], isLoading, isError } = useLoadCompanyDirectoryFullQuery({ includeTerminated: showTerminated });
  const { data: firms = [] } = useListFirmsQuery();
  const [upsert, upsertState] = useUpsertCompanyDirectoryEntryMutation();
  const [del] = useDeleteCompanyDirectoryEntryMutation();
  const [uploadPhoto] = useUploadCompanyDirectoryPhotoMutation();
  const [setActive] = useSetDirectoryActiveMutation();

  const [query, setQuery] = useState('');
  const [firmFilter, setFirmFilter] = useState('all');
  const [editing, setEditing] = useState(null);     // null = closed, object = open
  const [error, setError] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const firmOptions = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { if (r.firm) s.add(r.firm); });
    firms.forEach(f => { if (f.name) s.add(f.name); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows, firms]);

  const managers = useMemo(
    () => rows
      .filter(r => r.is_manager)
      .map(r => ({ id: r.id, first_name: r.first_name, last_name: r.last_name }))
      .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '')),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (firmFilter !== 'all' && (r.firm || '') !== firmFilter) return false;
      if (!q) return true;
      const hay = [
        r.first_name, r.last_name, r.final_email, r.prior_email,
        r.title, r.firm, r.location, r.phone, r.prior_company,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, firmFilter]);

  const openNew = () => { setError(null); setEditing(blankForm); };
  const openEdit = (r) => {
    setError(null);
    setEditing({
      id: r.id,
      first_name: r.first_name || '',
      last_name: r.last_name || '',
      final_email: r.final_email || '',
      prior_email: r.prior_email || '',
      prior_company: r.prior_company || '',
      title: r.title || '',
      firm: r.firm || '',
      location: r.location || '',
      phone: formatPhoneInput(r.phone || ''),
      hire_date: r.hire_date || '',
      date_of_birth: r.date_of_birth || '',
      is_manager: !!r.is_manager,
      manager_id: r.reports_to_board ? BOARD_OPTION : (r.manager_id || ''),
      can_own_projects: !!r.can_own_projects,
      is_advisor: !!r.is_advisor,
      show_calendar: !!r.show_calendar,
      is_active: r.is_active !== false,
      enabled_forms: Array.isArray(r.enabled_forms) ? r.enabled_forms : [],
      photo_url: r.photo_url || null,
    });
  };

  const save = async (form) => {
    setError(null);
    try {
      await upsert({
        ...form,
        hire_date: form.hire_date || null,
        date_of_birth: form.date_of_birth || null,
        // One dropdown, two stored fields. The Board is not a directory row, so
        // it cannot go in manager_id (FK-constrained) — it sets its own flag and
        // leaves manager_id null.
        manager_id: (form.manager_id && form.manager_id !== BOARD_OPTION)
          ? Number(form.manager_id) : null,
        reports_to_board: form.manager_id === BOARD_OPTION,
      }).unwrap();
      setEditing(null);
    } catch (e) {
      setError(e?.data?.detail || 'Save failed.');
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.first_name} ${r.last_name} from the directory?`)) return;
    try {
      await del({ id: r.id }).unwrap();
    } catch (e) {
      window.alert(e?.data?.detail || 'Delete failed.');
    }
  };

  const toggleActive = async (r) => {
    const next = !r.is_active;
    if (!next && !window.confirm(`Mark ${r.first_name} ${r.last_name} as terminated? They'll be hidden from the directory and all other pages (re-show them here with "Show terminated").`)) return;
    try {
      await setActive({ id: r.id, is_active: next }).unwrap();
    } catch (e) {
      window.alert(e?.data?.detail || 'Could not update status.');
    }
  };

  const onUploadPhoto = async (r, file) => {
    setUploadingId(r.id);
    try {
      await uploadPhoto({ directory_id: r.id, file }).unwrap();
    } catch (e) {
      window.alert(e?.data?.detail || 'Photo upload failed.');
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.75rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-person-lines-fill" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              HR Portal
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px' }}>Employee Directory</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: '0.875rem' }}>
              Add and update employee profiles shown on the company-wide directory.
            </p>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(10,41,71,0.06)', padding: '0.75rem 1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <i className="bi bi-search" style={{ color: brand.tealBlue }} />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Name, title, firm, location, email…"
          style={{ ...inputStyle, flex: 1, minWidth: 220 }}
        />
        <select
          value={firmFilter}
          onChange={e => setFirmFilter(e.target.value)}
          style={{ ...inputStyle, maxWidth: 180 }}
        >
          <option value="all">All firms</option>
          {firmOptions.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: brand.charcoal, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <input type="checkbox" checked={showTerminated} onChange={e => setShowTerminated(e.target.checked)} />
          Show terminated
        </label>
        <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>
          {filtered.length} of {rows.length}
        </span>
        <button
          type="button"
          onClick={openNew}
          style={{ background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: '0.85rem' }}
        >
          <i className="bi bi-plus-lg me-1" />Add Employee
        </button>
      </div>

      {isLoading ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div className="spinner-border" style={{ color: brand.tealBlue }} />
        </div>
      ) : isError ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          Unable to load the directory.
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
          <table className="table table-hover mb-0" style={{ fontSize: '0.85rem' }}>
            <thead style={{ background: brand.softGray }}>
              <tr>
                <th style={{ padding: '0.6rem 1rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Employee</th>
                <th style={{ padding: '0.6rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Title / Firm</th>
                <th style={{ padding: '0.6rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Location</th>
                <th style={{ padding: '0.6rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hire / DOB</th>
                <th style={{ padding: '0.6rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</th>
                <th style={{ padding: '0.6rem 1rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(emp => {
                const terminated = emp.is_active === false;
                return (
                <tr key={emp.id} style={terminated ? { background: '#faf7f3', opacity: 0.7 } : undefined}>
                  <td style={{ padding: '0.6rem 1rem', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <PhotoCell emp={emp} uploading={uploadingId === emp.id} onUpload={(file) => onUploadPhoto(emp, file)} />
                      <div style={{ fontWeight: 700, color: brand.deepBlue }}>
                        {emp.first_name} {emp.last_name}
                        {terminated && (
                          <span style={{ marginLeft: 8, background: '#e9e6e1', color: brand.charcoal, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '2px 7px', borderRadius: 999 }}>
                            Terminated
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '0.6rem', verticalAlign: 'middle', color: brand.charcoal }}>
                    <div>{emp.title || '—'}</div>
                    <div style={{ fontSize: '0.72rem', color: brand.tealBlue, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{emp.firm || ''}</div>
                  </td>
                  <td style={{ padding: '0.6rem', verticalAlign: 'middle', color: brand.charcoal }}>{emp.location || '—'}</td>
                  <td style={{ padding: '0.6rem', verticalAlign: 'middle', color: brand.charcoal, fontSize: '0.78rem' }}>
                    <div>Hire: {emp.hire_date || '—'}</div>
                    <div>DOB: {emp.date_of_birth || '—'}</div>
                  </td>
                  <td style={{ padding: '0.6rem', verticalAlign: 'middle', color: brand.charcoal }}>
                    <div>{emp.final_email || '—'}</div>
                    {emp.prior_email && <div style={{ fontSize: '0.75rem', color: brand.charcoal }}>{emp.prior_email}</div>}
                  </td>
                  <td style={{ padding: '0.6rem 1rem', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => toggleActive(emp)}
                      title={terminated ? 'Reactivate employee' : 'Mark as terminated'}
                      style={{ background: 'white', border: `1px solid ${brand.softGray}`, color: terminated ? brand.tealBlue : brand.charcoal, padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem', marginRight: 6 }}
                    >
                      <i className={`bi ${terminated ? 'bi-person-check' : 'bi-person-dash'}`} />
                    </button>
                    <button onClick={() => openEdit(emp)} style={{ background: 'white', border: `1px solid ${brand.softGray}`, color: brand.deepBlue, padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem', marginRight: 6 }}>
                      <i className="bi bi-pencil" />
                    </button>
                    <button onClick={() => remove(emp)} style={{ background: 'white', border: `1px solid ${brand.softGray}`, color: '#b00020', padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem' }}>
                      <i className="bi bi-trash" />
                    </button>
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
                  {query ? `No employees match "${query}".` : 'No employees yet — add one to get started.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
          saving={upsertState.isLoading}
          error={error}
          firms={firms}
          managers={managers}
          onUploadPhoto={async (file) => {
            if (!editing.id) return null;
            return uploadPhoto({ directory_id: editing.id, file }).unwrap();
          }}
        />
      )}
    </div>
  );
}
